import { useEffect, useCallback } from 'react';
import {
    Box,
    Checkbox,
    FormControl,
    ListItemText,
    MenuItem,
    OutlinedInput,
    Select,
    SelectChangeEvent
} from '@mui/material';
import Badge from '@mui/material/Badge';
import { DecisionReasonModel } from '../../lib/models/DecisionReasonModel';

const REASON_OUT_OF_SCOPE = 'Out of Scope';
const APPROVAL_STATUS_NO_REVIEW_REQUIRED = 'No Review Required';
const MULTISELECT_ARIA_LABEL = 'Select reasons';

export type DecisionReasonSelectProps = {
    procedureId: string;
    decisionReasons: DecisionReasonModel[] | null;
    disabled: boolean;
    required: boolean;
    reasons: DecisionReasonModel[] | null;
    handleReasonDenialChange: (value: DecisionReasonModel[], id: string) => void;
    approvalStatus: string | null;
};

function DecisionReasonSelect({
    procedureId,
    decisionReasons,
    reasons,
    required,
    handleReasonDenialChange,
    disabled,
    approvalStatus
}: DecisionReasonSelectProps) {
    // selected labels (since labels are unique)
    const selectedLabels = (decisionReasons ?? []).map((r) => r.label);

    // Helper to get DecisionReasonModel[] from labels
    const getReasonObjects = useCallback(
        (labels: string[]): DecisionReasonModel[] => {
            if (!reasons) return [];
            return reasons.filter((r) => labels.includes(r.label));
        },
        [reasons]
    );

    // Unified handler for changes
    const onReasonChange = useCallback(
        (labels: string[]) => {
            handleReasonDenialChange(getReasonObjects(labels), procedureId);
        },
        [getReasonObjects, handleReasonDenialChange, procedureId]
    );

    const handleChange = useCallback(
        (event: SelectChangeEvent<string[]>) => {
            let next = event.target.value as string[];
            if (!Array.isArray(next)) next = [String(next)];
            onReasonChange(next);
        },
        [onReasonChange]
    );

    // Auto-include "Out of Scope" for No Review Required
    useEffect(() => {
        if (approvalStatus !== APPROVAL_STATUS_NO_REVIEW_REQUIRED) return;
        if (!reasons) return;

        const oos = reasons.find((r) => r.name === REASON_OUT_OF_SCOPE);
        if (!oos) return;

        if (!selectedLabels.includes(oos.label)) {
            onReasonChange([...selectedLabels, oos.label]);
        }
    }, [approvalStatus, reasons, selectedLabels, onReasonChange]);

    // Auto-clear on approved/upheld
    useEffect(() => {
        if (approvalStatus === 'Approved' || approvalStatus === 'Upheld') {
            if (selectedLabels.length > 0) {
                onReasonChange([]);
            }
        }
    }, [approvalStatus, selectedLabels, onReasonChange]);

    return (
        <Badge badgeContent={selectedLabels.length} color="primary">
            <FormControl sx={{ margin: 0, width: 293 }} size="small" error={required}>
                <Select
                    sx={{
                        borderRadius: '8px',
                        color: (t) => t.palette.cardSubtitle.main,
                        fontWeight: 600,
                        fontSize: '14px'
                    }}
                    multiple
                    disabled={disabled}
                    displayEmpty
                    value={selectedLabels}
                    onChange={handleChange}
                    input={<OutlinedInput />}
                    inputProps={{
                        'aria-label': MULTISELECT_ARIA_LABEL
                    }}
                    renderValue={(labels) =>
                        labels.length === 0 ? 'Decision Reason' : (labels as string[]).join(', ')
                    }>
                    {(reasons ?? []).map((reason) => {
                        const checked = selectedLabels.includes(reason.label);
                        const isOutOfScope = reason.name === REASON_OUT_OF_SCOPE;
                        const isDisabled =
                            isOutOfScope && approvalStatus === APPROVAL_STATUS_NO_REVIEW_REQUIRED;

                        return (
                            <MenuItem
                                key={reason.label}
                                value={reason.label}
                                sx={isDisabled ? { pointerEvents: 'none' } : {}}>
                                <Checkbox checked={checked} disabled={isDisabled} />
                                <ListItemText primary={reason.label} />
                            </MenuItem>
                        );
                    })}
                </Select>

                {required && (
                    <Box sx={{ color: 'error.main', textAlign: 'center' }}>
                        Please select a reason
                    </Box>
                )}
            </FormControl>
        </Badge>
    );
}

export default DecisionReasonSelect;
